import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useCrmMessageFranchiseStats } from "../../statistics/hooks/useCrmMessageFranchiseStats";
import { endpointService } from "../services/endpointService";
import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";

/** Abaixo deste saldo restante exibimos alerta de poucas mensagens. */
export const CRM_LOW_REMAINING_MESSAGES_THRESHOLD = 10;

type EndpointResolution = "pending" | "active" | "inactive" | "failed";

export interface UseCrmSendMessageFranchiseGateParams {
  dialogOpen?: boolean;
  organizationId?: string;
  enforceMessageFranchiseGate?: boolean;
  franchiseRefreshSignal?: number;
  hasActiveMetaEndpoint?: boolean;
}

export interface UseCrmSendMessageFranchiseGateResult {
  isFranchiseCheckActive: boolean;
  requiresGate: boolean;
  hasActiveMetaEndpoint: boolean;
  hasMessagesForSend: boolean;
  remainingMessages: number;
  shouldShowPurchasePackages: boolean;
  showFranchisePurchaseOnlyView: boolean;
  hasLowRemainingMessages: boolean;
  isGateLoading: boolean;
  franchiseError: unknown;
  endpointLoadFailed: boolean;
  refetchFranchise: () => void;
  handlePurchaseCompleted: () => void;
  refetchEndpointCheck: () => void;
}

/**
 * Gate de franquia Fluxmed para envio WhatsApp.
 * Saldo: prioridade crm20 → crm50/crm100 (ver `resolveActiveCrmContractForSend`).
 */
export function useCrmSendMessageFranchiseGate(
  params: UseCrmSendMessageFranchiseGateParams = {},
): UseCrmSendMessageFranchiseGateResult {
  const {
    organizationId,
    enforceMessageFranchiseGate,
    franchiseRefreshSignal = 0,
    hasActiveMetaEndpoint: hasActiveMetaEndpointOverride,
    dialogOpen = false,
  } = params;

  const trimmedOrgId = organizationId?.trim() || undefined;
  const gateEnabled =
    Boolean(trimmedOrgId) && enforceMessageFranchiseGate !== false;
  const dialogOpenRef = useRef(false);
  const [openRefreshSignal, setOpenRefreshSignal] = useState(0);
  const [endpointResolution, setEndpointResolution] =
    useState<EndpointResolution>("pending");
  const [localFranchiseRefreshSignal, setLocalFranchiseRefreshSignal] =
    useState(0);

  const combinedFranchiseRefreshSignal =
    franchiseRefreshSignal + localFranchiseRefreshSignal + openRefreshSignal;

  const shouldFetchEndpoint =
    gateEnabled && hasActiveMetaEndpointOverride === undefined;

  const { run: runEndpointLoad, loading: isEndpointLoading } = useAsyncTask<
    [],
    boolean
  >({
    task: useCallback(async () => {
      const endpoint = await endpointService.getActiveWhatsAppEndpointByContext({
        organizationId: trimmedOrgId,
      });
      return Boolean(endpoint?.id && endpoint.status === "active");
    }, [trimmedOrgId]),
  });

  const resolveEndpointFromFetch = useCallback(() => {
    if (!gateEnabled || hasActiveMetaEndpointOverride !== undefined) return;

    setEndpointResolution("pending");
    void runEndpointLoad()
      .then((active) => {
        setEndpointResolution(active ? "active" : "inactive");
      })
      .catch(() => {
        setEndpointResolution("failed");
      });
  }, [gateEnabled, hasActiveMetaEndpointOverride, runEndpointLoad]);

  useEffect(() => {
    const justOpened = dialogOpen && !dialogOpenRef.current;
    dialogOpenRef.current = dialogOpen;
    if (!justOpened || !gateEnabled) return;

    setOpenRefreshSignal((prev) => prev + 1);
    resolveEndpointFromFetch();
  }, [dialogOpen, gateEnabled, resolveEndpointFromFetch]);

  const hasActiveMetaEndpoint = useMemo(() => {
    if (!gateEnabled) return false;
    if (hasActiveMetaEndpointOverride !== undefined) {
      return hasActiveMetaEndpointOverride;
    }
    return endpointResolution === "active";
  }, [gateEnabled, hasActiveMetaEndpointOverride, endpointResolution]);

  const endpointLoadFailed = useMemo(() => {
    if (hasActiveMetaEndpointOverride !== undefined) return false;
    return endpointResolution === "failed";
  }, [endpointResolution, hasActiveMetaEndpointOverride]);

  const requiresGate = gateEnabled && !hasActiveMetaEndpoint;

  // Carrega franquia ao abrir o dialog (independente do endpoint) para evitar race.
  const {
    stats,
    isLoading: isFranchiseLoading,
    loadError: franchiseError,
    refetch: refetchFranchise,
  } = useCrmMessageFranchiseStats(
    gateEnabled && dialogOpen ? trimmedOrgId : undefined,
    combinedFranchiseRefreshSignal,
  );

  const remainingMessages = useMemo(() => {
    const fromStats = stats.remainingMessagesForSend;
    if (typeof fromStats === "number" && Number.isFinite(fromStats)) {
      return Math.max(0, fromStats);
    }
    // Fallback só quando stats antigos não trazem remainingMessagesForSend.
    const available = stats.totalMessagesAvailable ?? 0;
    const used = stats.totalMessagesUsed ?? 0;
    return Math.max(0, available - used);
  }, [stats.remainingMessagesForSend, stats.totalMessagesAvailable, stats.totalMessagesUsed]);

  const hasMessagesForSend = useMemo(() => {
    if (!requiresGate) return true;
    return remainingMessages > 0;
  }, [requiresGate, remainingMessages]);

  const endpointResolutionSettled = useMemo(() => {
    if (hasActiveMetaEndpointOverride !== undefined) return true;
    return endpointResolution !== "pending";
  }, [endpointResolution, hasActiveMetaEndpointOverride]);

  const franchiseResolved =
    !isFranchiseLoading && franchiseError == null;

  /**
   * Compra só quando canal Fluxmed, franquia carregada OK, endpoint verificado
   * e nenhum tier (crm20 → crm50/crm100) tem saldo restante.
   */
  const shouldShowPurchasePackages =
    requiresGate &&
    franchiseResolved &&
    endpointResolutionSettled &&
    !endpointLoadFailed &&
    !hasMessagesForSend;

  const hasLowRemainingMessages = useMemo(() => {
    if (!requiresGate || !hasMessagesForSend) return false;
    return remainingMessages <= CRM_LOW_REMAINING_MESSAGES_THRESHOLD;
  }, [hasMessagesForSend, remainingMessages, requiresGate]);

  const isEndpointResolutionLoading =
    dialogOpen &&
    shouldFetchEndpoint &&
    (isEndpointLoading || endpointResolution === "pending");

  const isGateLoading =
    dialogOpen &&
    gateEnabled &&
    (isEndpointResolutionLoading ||
      (requiresGate && !franchiseResolved));

  const showFranchisePurchaseOnlyView =
    dialogOpen &&
    gateEnabled &&
    !isGateLoading &&
    shouldShowPurchasePackages;

  const handlePurchaseCompleted = useCallback(() => {
    setLocalFranchiseRefreshSignal((prev) => prev + 1);
    refetchFranchise();
  }, [refetchFranchise]);

  const refetchEndpointCheck = useCallback(() => {
    resolveEndpointFromFetch();
  }, [resolveEndpointFromFetch]);

  return {
    isFranchiseCheckActive: gateEnabled,
    requiresGate,
    hasActiveMetaEndpoint,
    hasMessagesForSend,
    remainingMessages,
    shouldShowPurchasePackages,
    showFranchisePurchaseOnlyView,
    hasLowRemainingMessages,
    isGateLoading,
    franchiseError,
    endpointLoadFailed,
    refetchFranchise,
    handlePurchaseCompleted,
    refetchEndpointCheck,
  };
}
